import {createColumnHelper} from "@tanstack/react-table";
import {truncateString} from "../../../Utils/StringHelper";
import {TanStackTable} from "../TanStackTable";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPencil, faTimes, faArrowRight} from "@fortawesome/free-solid-svg-icons";
import {useState, useEffect} from "react";
import {UpdateBranchDialog} from "../../Dialogs/Branches/UpdateBranchDialog";
import {CreateBranchDialog} from "../../Dialogs/Branches/CreateBranchDialog";
import {BlackButton} from "../../Buttons/BlackButton";
import {TextInput} from "../../Form/TextInput";
import {useBoundStore} from "../../../Store/store";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";

export const BranchesTable = (props) => {
    const {
        userId,
        forceNewData = false,
        shouldUpdate
    } = props;

    const [branches, setBranches] = useState([]);
    const [metaData, setMetaData] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);

    const getBranchesForUser = useBoundStore((state) => state.getBranchesForUser);
    const user = useBoundStore((state) => state.user);
    const {t} = useTranslation();

    useEffect(() => {
        const fetchBranches = async () => {
            setLoading(true);
            const result = await getBranchesForUser(userId, currentPage, search || null);
            setBranches(result.data);
            setMetaData(result.meta);
            setLoading(false);
        };

        if (userId) {
            fetchBranches();
        }
    }, [userId, currentPage, search, forceNewData, getBranchesForUser]);

    const handleUpdateDialog = () => {
        setIsUpdateDialogOpen(false);
        getBranchesForUser(userId, currentPage, search || null).then((result) => {
            setBranches(result.data);
            setMetaData(result.meta);
        });
        if (shouldUpdate) {
            shouldUpdate();
        }
    };

    const handleCreateDialog = () => {
        setCreateOpen(false);
        getBranchesForUser(userId, currentPage, search || null).then((result) => {
            setBranches(result.data);
            setMetaData(result.meta);
        });
        if (shouldUpdate) {
            shouldUpdate();
        }
    };

    const columnHelper = createColumnHelper();
    const columns = [
        columnHelper.accessor('Bedrijf', {
            header: t('tables.company'),
            cell: props => {
                return truncateString(props.row.original.company_name);
            },
        }),
        columnHelper.accessor('Contact', {
            header: t('tables.contact'),
            cell: props => {
                const branch = props.row.original;
                return `${branch?.contact_firstname ?? ''} ${branch?.contact_lastname ?? ''} (${branch?.contact_email ?? ''})`;
            },
        }),
        columnHelper.accessor('address', {
            header: t('tables.address'),
            cell: props => {
                const branch = props.row.original;
                return `${branch?.street ?? ''} ${branch?.number ?? ''} ${branch?.bus ?? ''}, ${branch?.postal ?? ''} ${branch?.city ?? ''} (${branch?.country ?? ''})`;
            },
        }),
        columnHelper.accessor('vat', {
            header: 'BTW Nr.',
            cell: props => `${truncateString(props.row.original.vat_number)}`,
        }),
        columnHelper.accessor('actions', {
            header: t('tables.actions'),
            cell: props => {
                const branch = props.row.original;
                const isAdmin = user?.roles?.includes('admin');

                return (
                    <div className={'flex flex-row space-x-4 text-right'}>
                        {isAdmin && (
                            <Link
                                to={`/admin/customers/${userId}/branches/${branch.id}`}
                                className={'flex bg-primary w-7 h-7 rounded-full text-white flex-col justify-center border border-primary hover:bg-transparent hover:border-primary hover:text-primary transition-all duration-300'}
                            >
                                <FontAwesomeIcon icon={faArrowRight} className={'text-sm'}/>
                            </Link>
                        )}
                        <button
                            className={'text-right'}
                            onClick={() => {
                                setSelectedBranch(branch);
                                setIsUpdateDialogOpen(true);
                            }}
                        >
                            <FontAwesomeIcon icon={faPencil} className={'mr-2'}/>
                        </button>
                    </div>
                );
            },
        }),
    ];

    return (
        <div>
            <div className={'flex flex-row justify-between mb-4'}>
                <h1 className={'text-xl font-bold my-auto'}>{t('branches.my_branches')}</h1>
                <BlackButton text={'secondary'} onClick={()=> setCreateOpen(true)}>
                    <FontAwesomeIcon icon={faPencil} className={'mr-2'}/> {t('branches.add_branch')}
                </BlackButton>
            </div>

            <div className={'mb-4'}>
                <div className={'grid grid-cols-1 md:grid-cols-3'}>
                    <div className={'my-auto relative'}>
                        <TextInput
                            type={'text'}
                            name={'search'}
                            translationIndex={null}
                            placeholder={t('branches.search_placeholder')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        {search && (
                            <button
                                onClick={() => setSearch('')}
                                className={'absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600'}
                                type="button"
                            >
                                <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {loading && (
                <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                </div>
            )}

            {!loading && branches.length === 0 && (
                <div className={'text-center text-gray-500 py-8'}>
                    {t('branches.no_results')}
                </div>
            )}

            {!loading && branches.length > 0 && (
                <TanStackTable
                    columns={columns}
                    laravelPaginationMetaData={metaData}
                    tableData={branches}
                    forceNewData={forceNewData}
                    onChangePage={(page) => {
                        setCurrentPage(page + 1)
                    }}
                />
            )}

            <UpdateBranchDialog
                open={isUpdateDialogOpen}
                onClose={handleUpdateDialog}
                branch={selectedBranch}
            />

            <CreateBranchDialog
                customerId={userId}
                open={createOpen}
                onClose={handleCreateDialog}
            />
        </div>
    );
};
